import React, { useState, useEffect } from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, MousePointer2, Play, RotateCcw, Search, Mail, BarChart3, Sparkles } from 'lucide-react';
import { TourStep, getStepSectionInfo } from '../../constants/tour';
import { tourSteps } from '../../constants/tourSteps';
import { useProductTour } from '../../contexts/ProductTourContext';
import { getEffectiveStepProps } from './tourUtils';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

const TOUR_STYLES = `
  @keyframes tourTooltipEnter {
    from { opacity: 0; transform: translateY(6px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes tourCompleteEnter {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes tourPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  @keyframes celebratePulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  @keyframes sparkle {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  .tour-tooltip { animation: tourTooltipEnter 0.25s ease-out forwards; }
  .tour-complete-tooltip { animation: tourCompleteEnter 0.4s ease-out forwards; }
  .tour-click-prompt { animation: tourPulse 1.5s ease-in-out infinite; }
  .tour-icon-pulse { animation: celebratePulse 2s ease-in-out infinite; }
  .tour-sparkle { animation: sparkle 1.5s ease-in-out infinite; }
  .tour-sparkle-2 { animation: sparkle 1.5s ease-in-out infinite 0.3s; }
  .tour-sparkle-3 { animation: sparkle 1.5s ease-in-out infinite 0.6s; }
`;

export const TourTooltip: React.FC<TooltipRenderProps> = ({
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  isLastStep,
  tooltipProps,
}) => {
  const navigate = useNavigate();
  const { startTour, endTour } = useProductTour();
  const tourStep = tourSteps[index] as TourStep;
  const effectiveProps = getEffectiveStepProps(tourStep);
  const isFirstStep = index === 0;
  const isTourComplete = tourStep?.name === 'tour-complete';
  const displayTitle = effectiveProps?.title || step.title;
  const displayContent = effectiveProps?.content || step.content;
  const requiresClick = effectiveProps?.requiresClick;
  const clickPrompt = effectiveProps?.clickPrompt;
  const sampleQuery = tourStep?.sampleQuery;

  const [hasSearchInput, setHasSearchInput] = useState(false);

  // Check if search input has content
  useEffect(() => {
    if (sampleQuery) {
      const checkInput = () => {
        const searchContainer = document.querySelector('[data-tour="search-input"]');
        const textarea = searchContainer?.querySelector('textarea') as HTMLTextAreaElement;
        setHasSearchInput(!!textarea?.value?.trim());
      };
      checkInput();
      const interval = setInterval(checkInput, 500);
      return () => clearInterval(interval);
    }
  }, [sampleQuery]);

  const handleUseSampleQuery = () => {
    if (!sampleQuery) return;

    const searchContainer = document.querySelector('[data-tour="search-input"]');
    if (!searchContainer) return;

    // Click placeholder button if exists to reveal textarea
    const placeholderButton = searchContainer.querySelector('button');
    if (placeholderButton) {
      placeholderButton.click();
    }

    setTimeout(() => {
      const textarea = searchContainer.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        // Use native setter to trigger React state update
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(textarea, sampleQuery);
        }
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.dispatchEvent(new Event('change', { bubbles: true }));
        setHasSearchInput(true);
      }
    }, 100);
  };

  const handleStartExploring = () => {
    endTour();
    navigate('/datasets');
  };

  const handleReplayTour = () => {
    endTour();
    setTimeout(() => startTour(), 300);
  };

  // Tour complete special rendering
  if (isTourComplete) {
    const isMobile = window.innerWidth < 768;

    return (
      <>
        <style>{TOUR_STYLES}</style>
        {isMobile && (
          <div className="fixed inset-0 bg-white/95 backdrop-blur-sm -z-10" />
        )}
        <div
          {...tooltipProps}
          className={`tour-complete-tooltip bg-white overflow-hidden fixed top-1/2 left-1/2 z-[10002] ${
            isMobile ? 'rounded-2xl w-[calc(100vw-32px)]' : 'rounded-[20px] w-[420px]'
          }`}
          style={{
            maxWidth: 'calc(100vw - 32px)',
            transform: 'translate(-50%, -50%)',
            boxShadow: isMobile
              ? '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)'
              : '0 32px 64px -16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Header */}
          <div
            className={`text-center relative overflow-hidden ${
              isMobile ? 'px-6 pt-7 pb-5' : 'px-8 pt-9 pb-7'
            }`}
            style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%)' }}
          >
            {/* Decorative sparkles */}
            <div className="tour-sparkle absolute top-6 left-12 w-2 h-2 rounded-full bg-amber-400" />
            <div className="tour-sparkle-2 absolute top-10 right-14 w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <div className="tour-sparkle-3 absolute top-[60px] left-[72px] w-[5px] h-[5px] rounded-full bg-blue-400" />

            {/* Logo */}
            <div
              className={`tour-icon-pulse mx-auto mb-4 bg-white flex items-center justify-center border border-black/5 ${
                isMobile ? 'w-16 h-16 rounded-2xl' : 'w-20 h-20 rounded-[20px]'
              }`}
              style={{ boxShadow: '0 8px 24px rgba(17, 24, 39, 0.15), 0 0 0 4px rgba(17, 24, 39, 0.05)' }}
            >
              <img
                src={skylarqLogo}
                alt="Skylarq"
                className={isMobile ? 'w-10 h-10' : 'w-[52px] h-[52px]'}
                style={{ objectFit: 'contain' }}
              />
            </div>

            <h3 className={`font-bold text-neutral-900 mb-2.5 leading-tight tracking-tight ${
              isMobile ? 'text-xl' : 'text-2xl'
            }`}>
              {step.title}
            </h3>
            <p className={`text-neutral-500 leading-relaxed mx-auto max-w-[300px] ${
              isMobile ? 'text-sm' : 'text-[15px]'
            }`}>
              {step.content}
            </p>
          </div>

          {/* Feature highlights */}
          <div className={`flex justify-center mb-1 ${isMobile ? 'px-5 gap-5' : 'px-8 gap-7'}`}>
            {[
              { Icon: Search, label: 'AI Search' },
              { Icon: Mail, label: 'Outreach' },
              { Icon: BarChart3, label: 'Analytics' },
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className={`bg-neutral-100 rounded-[10px] flex items-center justify-center mx-auto mb-1.5 ${
                  isMobile ? 'w-9 h-9' : 'w-10 h-10'
                }`}>
                  <feature.Icon size={isMobile ? 16 : 18} className="text-neutral-500" strokeWidth={1.75} />
                </div>
                <div className={`text-neutral-400 font-medium ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>
                  {feature.label}
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className={`flex flex-col gap-2.5 ${isMobile ? 'px-5 pt-4 pb-6' : 'px-8 pt-5 pb-7'}`}>
            <button
              onClick={handleStartExploring}
              className={`w-full bg-gradient-to-br from-neutral-900 to-neutral-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                isMobile ? 'py-3.5 text-sm' : 'py-4 text-[15px]'
              }`}
              style={{ boxShadow: '0 4px 16px rgba(17, 24, 39, 0.25)' }}
            >
              <Play size={isMobile ? 16 : 18} fill="white" />
              Start Exploring
            </button>
            <button
              onClick={handleReplayTour}
              className={`w-full bg-transparent border border-neutral-200 text-neutral-500 font-medium rounded-xl flex items-center justify-center gap-1.5 transition-all hover:bg-neutral-50 hover:border-neutral-300 ${
                isMobile ? 'py-3 text-[13px]' : 'py-3 text-sm'
              }`}
            >
              <RotateCcw size={isMobile ? 14 : 15} />
              Replay Tour
            </button>
          </div>
        </div>
      </>
    );
  }

  // Regular tooltip
  const sectionInfo = getStepSectionInfo(tourStep?.name);

  return (
    <div
      {...tooltipProps}
      className="tour-tooltip bg-white rounded-[14px] w-[300px] max-w-[calc(100vw-32px)] overflow-hidden"
      style={{ boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05)' }}
    >
      <style>{TOUR_STYLES}</style>

      {/* Content */}
      <div className="px-[18px] pt-4 pb-3.5">
        {/* Top row: Step counter + Close */}
        <div className="flex justify-between items-center mb-2.5">
          <span className="text-[11px] font-semibold text-neutral-400">
            {sectionInfo
              ? `${sectionInfo.label} · ${sectionInfo.stepInSection}/${sectionInfo.totalInSection}`
              : `Step ${index + 1} of ${tourSteps.length}`}
          </span>
          <button
            {...skipProps}
            className="p-0.5 text-neutral-400 hover:text-neutral-500 rounded transition-colors"
            aria-label="Close tour"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-semibold text-neutral-900 mb-1.5 leading-tight">
          {displayTitle}
        </h3>

        {/* Description */}
        <p className="text-[13px] leading-relaxed text-neutral-500">
          {displayContent}
        </p>

        {/* Sample query button */}
        {sampleQuery && !hasSearchInput && (
          <button
            onClick={handleUseSampleQuery}
            className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 px-3 bg-neutral-100 hover:bg-neutral-200 rounded-lg text-[12px] font-medium text-neutral-700 transition-colors"
          >
            <Sparkles size={12} />
            Use sample query
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="px-3.5 py-2.5 bg-neutral-50 border-t border-neutral-100 flex items-center justify-between gap-2.5">
        {/* Back */}
        <button
          {...backProps}
          disabled={isFirstStep}
          className={`w-[30px] h-[30px] flex items-center justify-center bg-white border border-neutral-200 rounded-md transition-colors ${
            isFirstStep ? 'opacity-40 cursor-default text-neutral-300' : 'cursor-pointer text-neutral-500 hover:bg-neutral-50'
          }`}
        >
          <ArrowLeft size={14} />
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action button (right-aligned) */}
        {requiresClick ? (
          <div className="tour-click-prompt flex items-center justify-center gap-1.5 py-1.5 px-3 bg-neutral-800 rounded-md text-[11px] font-medium text-white">
            <MousePointer2 size={11} />
            {clickPrompt || 'Click'}
          </div>
        ) : (
          <button
            {...primaryProps}
            className="flex items-center justify-center gap-1 py-[7px] px-3.5 bg-neutral-800 rounded-md text-xs font-medium text-white cursor-pointer transition-colors hover:bg-neutral-700"
          >
            {isLastStep ? 'Finish' : 'Next'}
            <ArrowRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
};
